import Card from '@material-ui/core/Card';
import { blue, green, red } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import WebVersionsService from '../../services/WebVersionsService';
import VersionsLoadingSkeleton from './VersionsLoadingSkeleton';
import { useTranslation } from 'react-i18next';

const CHANGELOG_CACHE_KEY = 'home-changelog-cache';

const useStyles = makeStyles(theme => ({
    root: {
        padding: '24px 32px',
    },
    badge: {
        display: 'inline-flex',
        fontSize: 13,
        color: '#FFF',
        letterSpacing: '.015em',
        lineHeight: 1,
        padding: '5px 8px',
        borderRadius: 2,
        '&.newChanges': {
            backgroundColor: green[500],
        },
        '&.fixedChanges': {
            backgroundColor: blue[500],
        },
        '&.breakingChanges': {
            backgroundColor: red[500],
        },
    },
    title: {
        flexGrow: 1,
    },
}));

function HomeVersionCard(props) {
    const { t, i18n } = useTranslation();
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState(true);
    const [changelogData, setChangelogData] = useState([]);

    useEffect(() => {
        let cancelled = false;

        try {
            const cached = sessionStorage.getItem(CHANGELOG_CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setChangelogData(parsed);
                    setIsLoading(false);
                }
            }
        } catch (e) {
            // ignore invalid cache
        }

        const loadChangelog = () => {
            WebVersionsService.getAllWithChanges()
                .then(response => {
                    const data = Array.isArray(response.data) ? response.data : [];
                    if (data.length > 0) {
                        try {
                            sessionStorage.setItem(
                                CHANGELOG_CACHE_KEY,
                                JSON.stringify(data)
                            );
                        } catch (e) {
                            // ignore quota errors
                        }
                    }
                    if (cancelled) return;
                    setChangelogData(data);
                })
                .catch(e => {
                    if (!cancelled) {
                        console.error('Failed to load changelog', e);
                    }
                })
                .finally(() => {
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                });
        };

        loadChangelog();

        return () => {
            cancelled = true;
        };
    }, []);

    // Function to get the description in the current language
    const getLocalizedDescription = change => {
        const currentLanguage = i18n.language;

        if (currentLanguage === 'es' && change.description_es) {
            return change.description_es;
        } else if (currentLanguage === 'en' && change.description_en) {
            return change.description_en;
        }

        // Fallback to default description
        return change.description;
    };

    return (
        <Card className={clsx(classes.root, props.className)} elevation={1}>
            {isLoading ? (
                <VersionsLoadingSkeleton />
            ) : (
                changelogData.map(item => (
                    <div key={item.version_id}>
                        <div className="flex items-center">
                            {item.version_number && (
                                <Typography
                                    component="h6"
                                    variant="h6"
                                    color="inherit"
                                    className={classes.title}
                                >
                                    {`v${item.version_number}`}
                                </Typography>
                            )}
                            {item.release_date && (
                                <Typography
                                    className={classes.title}
                                    component="h3"
                                >
                                    ({item.release_date})
                                </Typography>
                            )}
                        </div>
                        <br />
                        {item.changes &&
                            Object.keys(item.changes).map(category => (
                                <div key={category}>
                                    <div
                                        className={clsx(
                                            classes.badge,
                                            category
                                        )}
                                    >
                                        {category === 'newChanges'
                                            ? t('home.newChanges')
                                            : t('home.fixedChanges')}
                                    </div>
                                    <ul className="my-16 px-24">
                                        {item.changes[category].map(
                                            (change, index) => (
                                                <li
                                                    key={
                                                        change.change_id ||
                                                        index
                                                    }
                                                    className="mb-6"
                                                >
                                                    <Typography>
                                                        {getLocalizedDescription(
                                                            change
                                                        )}
                                                    </Typography>
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            ))}
                        {item.notes}
                    </div>
                ))
            )}
        </Card>
    );
}

HomeVersionCard.defaultProps = {
    version: null,
    date: null,
    newChanges: [],
    fixedChanges: [],
    breakingChanges: [],
    notes: null,
};
export default HomeVersionCard;
